import type { NexoyaContentLabelStatus } from '../../../../types';

import { type LabelAssignmentState, toAssignmentState } from '../../../settings/labels/data/labelCategoryMapper';

/**
 * Content Labels v2 on the portfolio Content tab — the page's
 * `listContentsByIds` answer turned into one picker row per (content, category).
 *
 * Pure, so it runs in the node project: the cell, the writes hook and the query
 * are three separate modules precisely so the row maths can be tested without a
 * DOM or an Apollo provider.
 *
 * `ContentV2.labelAssignments` takes NO arguments — it answers for every
 * category of the team — so the index is two levels deep and each column reads
 * only its own category's entry. Nothing here re-derives a stage from a
 * `labelStatus`: `toAssignmentState` owns that mapping for both surfaces.
 */

/** A label reference as `ContentLabelValueRef` selects it: the entity key alone. */
type ValueRef = { contentLabelCategoryValueId: string };

/** One entry of `ContentV2.labelAssignments`, as this tab's document selects it. */
export type ContentLabelAssignmentPayload = {
  labelStatus: NexoyaContentLabelStatus;
  contentLabelCategory: { contentLabelCategoryId: string };
  assignment?: ValueRef | null;
  matches?: ValueRef[] | null;
};

/** One `ContentV2` of the answer, and equally one row of the table on screen. */
export type ContentLabelAssignmentsContent = {
  contentId: number | string;
  title?: string | null;
  labelAssignments?: ContentLabelAssignmentPayload[] | null;
};

/**
 * The picker's row, structurally — NOT the settings `LabelAssignmentRow`.
 *
 * `components-ui/RowLabelPicker` may not import from `routes/`, so it takes this
 * shape; the settings row stays assignable to it, which is what lets one
 * component serve both surfaces.
 *
 * `content.contentId` is the STRING form, because that is the only form
 * `toAssignableContentIds` accepts back on the write.
 */
export type ContentLabelCellRow = LabelAssignmentState & {
  content: { contentId: string; title: string };
};

/** `contentId` (string) → `contentLabelCategoryId` → that pair's row. */
export type ContentLabelRowIndex = Map<string, Map<string, ContentLabelCellRow>>;

/** The one canonical form of a content id in this feature — see `toContentIdNumber`. */
const contentKey = (contentId: number | string): string => String(contentId ?? '');

const toCellContent = (content: Pick<ContentLabelAssignmentsContent, 'contentId' | 'title'>) => ({
  contentId: contentKey(content.contentId),
  // A nullable `title` would otherwise reach the picker's accessible names as
  // the literal "null" ("Add Country label to null").
  title: content.title ?? '',
});

/** The row a content shows in a category it was never classified against — see `contentLabelRow`. */
function unlabeledRow(content: Pick<ContentLabelAssignmentsContent, 'contentId' | 'title'>): ContentLabelCellRow {
  return {
    content: toCellContent(content),
    stage: 'unlabeled',
    matchIds: [],
    appliedLabelId: null,
    setByHand: false,
  };
}

/**
 * Every (content, category) row the page's answer carries.
 *
 * An assignment with no category — impossible on the wire, cheap to survive — is
 * dropped rather than indexed under `undefined`, where it would answer for the
 * first column that asked.
 */
export function toContentLabelRowIndex(
  contents: ContentLabelAssignmentsContent[] | null | undefined,
): ContentLabelRowIndex {
  const index: ContentLabelRowIndex = new Map();

  (contents ?? []).forEach((content) => {
    const byCategory = new Map<string, ContentLabelCellRow>();

    (content.labelAssignments ?? []).forEach((assignment) => {
      const categoryId = assignment?.contentLabelCategory?.contentLabelCategoryId;
      if (!categoryId) return;

      byCategory.set(categoryId, { content: toCellContent(content), ...toAssignmentState(assignment) });
    });

    index.set(contentKey(content.contentId), byCategory);
  });

  return index;
}

/**
 * The row one cell draws — ALWAYS a row, never `undefined`.
 *
 * Two things are routinely missing and neither is an error:
 * - the content, while the page's assignments read is still in flight;
 * - the category's entry, because `labelAssignments` promises a list, not one
 *   entry per category (a category created after the content was last
 *   classified simply has none).
 *
 * Both mean "unlabeled in this category", and the cell has to draw
 * "+ Add label" for them: answering `undefined` would render a blank strip and
 * take the row's only affordance away.
 */
export function contentLabelRow(
  index: ContentLabelRowIndex,
  content: Pick<ContentLabelAssignmentsContent, 'contentId' | 'title'>,
  contentLabelCategoryId: string,
): ContentLabelCellRow {
  return index.get(contentKey(content.contentId))?.get(contentLabelCategoryId) ?? unlabeledRow(content);
}
